<template>
  <div class="bg-white rounded-xl border">
    <div
      class="px-4 py-4 border-b rounded-t-lg border-neutral-200"
      id="board-dropdown-container"
    >
      <h3 v-if="suggestionsShown" class="flex items-center">
        <span class="text-sm text-neutral-800 bg-neutral-100 rounded-xl px-2 py-1">✨ {{ $t("We found some similar ideas") }}</span>
      </h3>

      <div
        v-if="currentProjectAdmin && !suggestionsShown && !this.nofilter"
        class="flex flex-wrap items-center justify-between gap-2 mb-3"
        id="board-view-tabs-row"
      >
        <TabGroup
          :tabs="viewTabs"
          :value="viewTab"
          @input="changeViewTab"
          id="board-view-tabs"
        >
          <template #badge="{ tab, active }">
            <span
              v-if="tab.name === 'ideas' && ideasCount !== null"
              class="text-xs font-semibold px-1.5 py-0.5 rounded-full"
              :class="active ? 'bg-neutral-100 text-neutral-700' : 'bg-neutral-200 text-neutral-600'"
              :title="ideasCount.toLocaleString()"
            >{{ formatCount(ideasCount) }}</span>
            <span
              v-if="tab.name === 'spam' && spamCount > 0"
              class="text-xs font-semibold px-1.5 py-0.5 rounded-full"
              :class="active ? 'bg-red-100 text-red-700' : 'bg-neutral-200 text-neutral-600'"
              :title="spamCount.toLocaleString()"
            >{{ formatCount(spamCount) }}</span>
          </template>
        </TabGroup>
        <div v-if="$slots.manage" class="relative" ref="manageRoot" id="board-manage-menu">
          <button
            type="button"
            class="border bg-white border-neutral-300 rounded-lg bg-gradient-to-b from-white to-neutral-100 shadow-sm px-3 py-1 text-sm flex items-center whitespace-nowrap text-neutral-800 focus:to-white focus:border-neutral-400"
            id="board-manage-button"
            @click="manageOpen = !manageOpen"
          >
            <svg class="w-4 h-4 mr-1 text-dblue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            {{ $t("Manage board") }}
            <svg class="w-4 h-4 ml-1 -mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
          </button>
          <transition name="popdown">
            <div
              v-if="manageOpen"
              class="absolute right-0 mt-2 w-52 bg-white border border-dblue-100 rounded-lg shadow-lg z-[9999] py-1.5"
              id="board-manage-panel"
            >
              <slot name="manage"></slot>
            </div>
          </transition>
        </div>
      </div>

      <div
        v-if="!suggestionsShown && !this.nofilter"
        class="flex items-stretch sm:items-center justify-between flex-col sm:flex-row gap-2 text-sm"
        id="all-dropdown-board"
      >
        <div class="flex items-stretch sm:items-center justify-start flex-col sm:flex-row gap-2">
          <SelectOneDropdown
            :name="$t('Order')"
            :values="sortOptions"
            :currentValue="sort"
            @valueChange="changeSort"
            class="order-dropdown-board"
            id="order-dropdown-board"
          >
            <svg
              slot="icon"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
              ></path>
            </svg>
          </SelectOneDropdown>
          <SelectOneDropdown
            v-if="viewTab !== 'spam'"
            :name="$t('Status')"
            :values="statusComp"
            :default-status="defaultStatuses"
            :currentValue="filter"
            @valueChange="changeFilter"
            :isAdmin="currentProjectAdmin"
            class="status-dropdown-board"
            id="status-dropdown-board"
          >
            <div
              slot="icon"
              class="w-2 h-2 mr-1 rounded-full"
              :style="
                'background-color: ' + (currentFilter && currentFilter.color)
              "
            ></div>
          </SelectOneDropdown>
          <SelectMultipleDropdown
            v-if="showTags"
            :all-tags="tags"
            :selected-tags="selectedTags"
            @tagsChange="updateSelectedTags"
            class="tags-dropdown-board"
            id="tags-dropdown-board"
          >
            <simple-svg
              slot="icon"
              :filepath="tagsIcon"
              width="16px"
              height="16px"
            />
          </SelectMultipleDropdown>
          <SubmitterFilter
            v-if="currentProjectAdmin"
            :board-id="postBoard.id"
            :value="submitterFilter"
            @change="changeSubmitterFilter"
            class="submitter-dropdown-board"
            id="submitter-dropdown-board"
          />
        </div>

        <div class="flex items-stretch sm:items-center gap-2 flex-col sm:flex-row sm:ml-auto" id="my-posts-filter">
          <SelectOneDropdown
            v-if="viewTab !== 'spam'"
            :name="$t('Filter')"
            :values="filterOptions"
            :currentValue="selectedFilter"
            @valueChange="changeSelectedFilter"
            class="filter-dropdown-board w-full"
            id="filter-dropdown-board"
          >
            <svg
              slot="icon"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
            </svg>
          </SelectOneDropdown>
          <button
            v-if="currentProjectAdmin"
            type="button"
            class="border rounded-lg shadow-sm px-3 py-1 flex items-center justify-center whitespace-nowrap"
            :class="selectMode ? 'bg-neutral-100 border-neutral-400' : 'bg-white border-neutral-300 bg-gradient-to-b from-white to-neutral-100 focus:to-white focus:border-neutral-400'"
            id="bulk-select-toggle"
            @click="toggleSelectMode"
          >
            <svg
              class="w-4 h-4 mr-1"
              :class="selectMode ? 'text-neutral-800' : 'text-dblue-400'"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span :class="selectMode ? 'text-neutral-900 font-medium' : 'text-neutral-800'">{{ selectMode ? $t('Done') : $t('Select') }}</span>
          </button>
        </div>
      </div>

      <h3 v-if="this.nofilter">
        <slot name="header"></slot>
      </h3>
    </div>
    <div
      v-if="viewTab === 'spam' && noPosts && !showLoading"
      class="flex flex-col items-center justify-center p-10 text-center"
      id="no-spam-message"
    >
      <span class="text-2xl mb-2">🎉</span>
      <p class="text-neutral-800 font-medium">{{ $t('No spam here') }}</p>
      <p class="text-sm text-neutral-500 mt-1">{{ $t('Posts flagged as spam will show up in this tab') }}</p>
    </div>

    <div
      v-if="viewTab !== 'spam' && noPosts && !showLoading"
      class="flex flex-col items-center justify-center p-10"
      id="no-post-message"
    >
      <div class="flex flex-col gap-5 w-full max-w-sm mx-auto">
        <div class="opacity-50 border-2 border-neutral-200 p-5 rounded-xl w-full flex items-center">
          <div class="p-1 bg-neutral-200 rounded-xl flex items-center">
              <svg
                class="w-8 h-8 opacity-20"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
            </svg>
          </div>
          <div class="flex-grow ml-4">
            <div class="h-4 bg-neutral-200 rounded w-1/2"></div>
            <div class="h-4 bg-neutral-200 rounded w-3/4 mt-2"></div>
          </div>
        </div>
        <div class="border-2 p-5 rounded-xl w-full flex items-center shadow-lg" style="border-color: var(--accent-color)">
          <div class="p-1 bg-neutral-200 rounded-xl flex items-center">
              <svg
                class="w-8 h-8 opacity-20"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
            </svg>
          </div>
          <div class="flex-grow ml-4">
            <div class="h-4 bg-neutral-200 rounded w-1/2"></div>
            <div class="h-4 bg-neutral-200 rounded w-3/4 mt-2"></div>
          </div>
        </div>
        <div class="opacity-50 border-2 border-neutral-200 p-5 rounded-xl w-full flex items-center">
          <div class="p-1 bg-neutral-200 rounded-xl flex items-center">
              <svg
                class="w-8 h-8 opacity-20"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
            </svg>
          </div>
          <div class="flex-grow ml-4">
            <div class="h-4 bg-neutral-200 rounded w-1/2"></div>
            <div class="h-4 bg-neutral-200 rounded w-3/4 mt-2"></div>
          </div>
        </div>
      </div>
      <p class="mt-6 text-black font-medium">
        {{ $t("Let's add some ideas to get started") }}
      </p>
    </div>

    <div
      v-if="viewTab !== 'spam' && sortedAndFilteredPosts.length == 0 && posts.length !== 0 && !showLoading"
      class="flex flex-col items-center justify-center p-10"
      id="no-post-fit-message"
    >
      <div class="flex flex-col gap-5 w-full max-w-sm mx-auto">
        <div class="opacity-50 border-2 border-neutral-200 p-5 rounded-xl w-full flex items-center">
          <div class="p-1 bg-neutral-200 rounded-xl flex items-center">
              <svg
                class="w-8 h-8 opacity-20"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
            </svg>
          </div>
          <div class="flex-grow ml-4">
            <div class="h-4 bg-neutral-200 rounded w-1/2"></div>
            <div class="h-4 bg-neutral-200 rounded w-3/4 mt-2"></div>
          </div>
        </div>
        <div class="border-2 p-5 rounded-xl w-full flex items-center shadow-lg" style="border-color: var(--accent-color)">
          <div class="p-1 bg-neutral-200 rounded-xl flex items-center">
              <svg
                class="w-8 h-8 opacity-20"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
            </svg>
          </div>
          <div class="flex-grow ml-4">
            <div class="h-4 bg-neutral-200 rounded w-1/2"></div>
            <div class="h-4 bg-neutral-200 rounded w-3/4 mt-2"></div>
          </div>
        </div>
        <div class="opacity-50 border-2 border-neutral-200 p-5 rounded-xl w-full flex items-center">
          <div class="p-1 bg-neutral-200 rounded-xl flex items-center">
              <svg
                class="w-8 h-8 opacity-20"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
            </svg>
          </div>
          <div class="flex-grow ml-4">
            <div class="h-4 bg-neutral-200 rounded w-1/2"></div>
            <div class="h-4 bg-neutral-200 rounded w-3/4 mt-2"></div>
          </div>
        </div>
      </div>
      <p class="mt-6 text-black font-medium">
        {{ $t("No ideas fit chosen criteria") }}
      </p>
      <p class="mt-2 text-neutral-800">
        {{ $t("Try changing the filters or adding a new idea") }}
      </p>
    </div>

    <transition-group
      v-show="!showLoading && sortedAndFilteredPosts.length > 0"
      name="list"
      class="flex flex-col fade-up-in overflow-hidden rounded-xl"
      :class="{ 'select-none': selectMode }"
      tag="div"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="isLoadingMore || !hasMore || suggestionsShown"
      infinite-scroll-distance="200"
      id="all-posts"
      appear
    >
      <div
        is="post"
        v-for="post in paginatedPosts"
        :originalPost="post"
        :currentUser="currentUser"
        :current-project-admin="currentProjectAdmin"
        :show-board="showBoard"
        :key="post.id"
        :allow-anonymous="allowAnonymous"
        :allow-vote-count="allowVoteCount"
        :status-name="statusName"
        :bulk-selectable="currentProjectAdmin && !nofilter && selectMode"
        :bulk-selected="selectedPostIds.includes(post.id)"
        @toggle-bulk="toggleBulkSelect"
        :id="'board-post-' + post.id"
        :class="'board-post-' + post.id"
        is-board="isBoard"
      ></div>
    </transition-group>

    <!-- Bulk action bar (admins, appears with a selection) -->
    <transition name="bulkbar">
    <div
      v-if="selectedPostIds.length > 0"
      class="sticky bottom-4 z-40 px-4 pb-4"
      id="bulk-action-bar"
    >
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-neutral-200 rounded-xl shadow-lg px-4 py-3">
        <div class="flex items-center gap-3 flex-wrap">
          <span class="text-sm font-semibold text-neutral-800 whitespace-nowrap">{{ selectedPostIds.length }} {{ $t('selected') }}</span>
          <button type="button" class="text-sm font-medium text-neutral-600 hover:text-neutral-900 underline-offset-2 hover:underline" @click="selectAllOnPage">{{ $t('Select page') }}</button>
          <button type="button" class="text-sm font-medium text-neutral-600 hover:text-neutral-900 underline-offset-2 hover:underline" @click="clearBulkSelection">{{ $t('Clear') }}</button>
          <span v-if="bulkError" class="text-sm text-red-600">{{ bulkError }}</span>
        </div>
        <div class="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:flex-wrap">
          <SelectOneDropdown
            :name="$t('Set status')"
            :values="bulkStatusValues"
            :default-status="bulkStatusPlaceholder"
            currentValue=""
            direction="up"
            :isAdmin="currentProjectAdmin"
            @valueChange="onBulkStatusPick"
            class="bulk-status-dropdown"
            id="bulk-status-dropdown"
          >
            <div slot="icon" class="w-2 h-2 mr-1 rounded-full bg-neutral-300"></div>
          </SelectOneDropdown>
          <button
            v-if="viewTab === 'spam'"
            type="button"
            class="text-white text-sm font-medium rounded-lg shadow-sm px-4 py-2"
            style="background-color: var(--accent-color)"
            :disabled="bulkBusy"
            @click="runBulk('not_spam')"
          >{{ $t('Not spam') }}</button>
          <button
            v-else
            type="button"
            class="bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-sm font-medium rounded-lg shadow-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="bulkBusy"
            @click="runBulk('spam')"
          >{{ $t('Mark as spam') }}</button>
          <button
            v-if="viewTab !== 'spam'"
            type="button"
            class="bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-sm font-medium rounded-lg shadow-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="bulkBusy || selectedPostIds.length < 2 || selectedPostIds.length > bulkMergeLimit"
            @click="openBulkMergeDialog"
          >{{ selectedPostIds.length > bulkMergeLimit ? $t('Merge (max 20)') : $t('Merge') }}</button>
          <button
            type="button"
            class="bg-white border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg shadow-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="bulkBusy || selectedPostIds.length > bulkDeleteLimit"
            @click="showBulkDeleteConfirm = true"
          >{{ selectedPostIds.length > bulkDeleteLimit ? $t('Delete (max 50)') : $t('Delete') }}</button>
        </div>
      </div>
    </div>
    </transition>

    <!-- Bulk delete confirmation -->
    <Dialog :title="$t('Delete ideas')" v-model="showBulkDeleteConfirm">
      <div class="px-4 pt-2 pb-4 sm:px-6 sm:pb-4">
        <p class="text-neutral-600">
          {{ selectedPostIds.length }} {{ $t('ideas will be permanently deleted, along with their votes and comments') }}.
          {{ $t("This action can't be undone.") }}
        </p>
      </div>
      <div class="px-4 py-3 sm:px-6 flex items-center justify-end gap-3">
        <button
          type="button"
          class="bg-white border border-gray-300 text-neutral-800 hover:bg-gray-50 rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
          @click="showBulkDeleteConfirm = false"
        >{{ $t('Cancel') }}</button>
        <button
          type="button"
          class="bg-red-600 hover:bg-red-500 text-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
          :disabled="bulkBusy"
          @click="runBulk('delete')"
        >{{ $t('Delete') }}</button>
      </div>
    </Dialog>

    <!-- Bulk merge confirmation -->
    <Dialog :title="$t('Merge ideas')" v-model="showBulkMergeConfirm">
      <div class="px-4 pt-2 pb-4 sm:px-6 sm:pb-4">
        <p class="text-neutral-600 mb-3">
          {{ $t('Select the idea to keep. The others will be merged into it and their votes and comments move over.') }}
          {{ $t("This action can't be undone.") }}
        </p>
        <div class="max-h-64 overflow-y-auto flex flex-col gap-2" role="radiogroup">
          <label
            v-for="candidate in bulkMergeCandidates"
            :key="candidate.id"
            class="flex items-center gap-3 border rounded-lg px-3 py-2 cursor-pointer transition-colors"
            :class="candidate.id === bulkMergeTargetId ? 'bg-project-50' : 'border-neutral-200 hover:bg-neutral-50'"
            :style="candidate.id === bulkMergeTargetId ? { borderColor: 'var(--accent-color)' } : {}"
          >
            <input
              type="radio"
              name="bulk-merge-target"
              class="shrink-0"
              :value="candidate.id"
              v-model="bulkMergeTargetId"
            >
            <span class="flex-1 text-sm text-neutral-800 truncate">{{ candidate.title }}</span>
            <span class="shrink-0 text-xs font-medium text-neutral-500 whitespace-nowrap">{{ candidate.votes }} {{ candidate.votes === 1 ? $t('vote') : $t('votes') }}</span>
          </label>
        </div>
      </div>
      <div class="px-4 py-3 sm:px-6 flex items-center justify-end gap-3">
        <button
          type="button"
          class="bg-white border border-gray-300 text-neutral-800 hover:bg-gray-50 rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
          @click="showBulkMergeConfirm = false"
        >{{ $t('Cancel') }}</button>
        <button
          type="button"
          class="text-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium disabled:opacity-50"
          style="background-color: var(--accent-color)"
          :disabled="bulkBusy || !bulkMergeTargetId"
          @click="runBulk('merge', null, bulkMergeTargetId, bulkMergeCandidates.map(c => c.id))"
        >{{ $t('Merge') }}</button>
      </div>
    </Dialog>

    <!-- Loading more indicator -->
    <div v-if="isLoadingMore && !showLoading" class="p-4 text-center">
      <div class="lds-ripple text-project-500 scale-75"><div></div><div></div></div>
    </div>

    <div v-if="showLoading">
    <div class="p-5">
      <div class="flex justify-center">
        <div class="lds-ripple text-project-500"><div></div><div></div></div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
var infiniteScroll = require("vue-infinite-scroll");

import Post from "./post.vue";
import noPostsIcon from "images/empty_posts.png";
import tagsIcon from "images/tags.svg";
import EventBus from "event_bus";
import SelectOneDropdown from "./components/select_one_dropdown";
import SelectMultipleDropdown from "./components/select_multiple_dropdown";
import Dialog from "./components/dialog";
import TabGroup from "./components/tab_group";
import SubmitterFilter from "./components/submitter_filter";

// Mirror the server-side caps in posts_controller.rb (BULK_ACTION_LIMIT / BULK_DELETE_LIMIT / BULK_MERGE_LIMIT).
const BULK_SELECT_LIMIT = 500;
const BULK_DELETE_LIMIT = 50;
const BULK_MERGE_LIMIT = 20;

export default {
  props: [
    "current-user",
    "current-project-admin",
    "nofilter",
    "show-board",
    "all-tags",
    "show-tags",
    "allow-anonymous",
    "post-status",
    "status-ids",
    "allow-vote-count",
    "status-name",
    "post-board",
    "is-board",
  ],
  data: function () {
    return {
      // Post data
      posts: [],
      currentUserVotes: [],

      // UI state
      noPosts: false,
      showLoading: true,
      suggestionsShown: false,

      // Filter state
      sort: "trend_score",
      filter: "Not done",
      selectedFilter: "All",
      selectedTags: [],
      submitterFilter: null,
      tags: [],
      defaultStatuses: [],

      // Admin spam view + bulk selection
      ideasCount: null,
      manageOpen: false,
      viewTab: "ideas",
      spamCount: null,
      selectMode: false,
      selectedPostIds: [],
      lastSelectedIndex: null,
      bulkBusy: false,
      bulkError: "",
      showBulkDeleteConfirm: false,
      showBulkMergeConfirm: false,
      bulkMergeTargetId: null,

      // Server-side pagination state
      nextCursor: null,
      hasMore: true,
      isLoadingMore: false,
      initialLoadComplete: false,

      // Request management
      fetchController: null,
      fetchDebounceTimer: null,

      // Assets
      noPostsIcon,
      tagsIcon,
    };
  },
  directives: { infiniteScroll },
  components: {
    Post,
    noPostsIcon,
    SelectOneDropdown,
    SelectMultipleDropdown,
    Dialog,
    TabGroup,
    SubmitterFilter,
  },
  async created() {
    // Board's configured default sort, unless a URL param overrides it below
    const boardDefault = this.postBoard && this.postBoard.default_sort;
    if (boardDefault && this.sortOptions.some((o) => o.name === boardDefault)) {
      this.sort = boardDefault;
    }

    // Apply URL params before first fetch
    this.applyUrlParams();

    // Initial fetch
    await this.fetchPosts(true);

    // Set up default statuses for dropdown
    this.initializeStatusDropdown();
  },
  mounted: function () {
    // Initialize tags
    var all_tags = this.allTags
      ? this.allTags.map((tag) => tag.name.trim())
      : [];
    this.tags = [...new Set(all_tags)];

    // Set up event listeners
    this.setupEventListeners();

    // Close the manage-board menu on outside clicks
    this.manageOutsideHandler = (event) => {
      if (this.manageOpen && this.$refs.manageRoot && !this.$refs.manageRoot.contains(event.target)) {
        this.manageOpen = false;
      }
    };
    window.addEventListener("click", this.manageOutsideHandler, true);
  },
  computed: {
    sortOptions() {
      return [
        { name: "trend_score", human_name: this.$t("Trending") },
        { name: "total_upvotes", human_name: this.$t("Most votes") },
        { name: "created_at", human_name: this.$t("Recent") },
        { name: "comments_count", human_name: this.$t("Most discussed") },
      ];
    },
    statusComp() {
      const filterAllStatus = [];
      this.statusIds.forEach((id) => {
        const result = this.postStatus.find(
          (status) => status.id === id && !this.defaultStatuses.includes(status)
        );
        if (result) {
          filterAllStatus.push(result);
        }
      });
      const notSet = this.postStatus.filter(
        (status) => status.name === this.statusName.not_set
      );
      if (notSet[0]) {
        notSet[0].human_name = "Not set";

        if (this.postBoard.hide_not_set_status) {
          if (this.currentProjectAdmin)
            this.defaultStatuses.splice(2, 0, notSet[0]);
        } else {
          this.defaultStatuses.splice(2, 0, notSet[0]);
        }
      }

      return filterAllStatus.map((status) => {
        status.human_name = status.name;
        return status;
      });
    },
    // Server handles sorting/filtering, just return loaded posts
    sortedAndFilteredPosts: function () {
      return this.posts;
    },
    // Server already paginates, return all loaded posts
    paginatedPosts: function () {
      return this.sortedAndFilteredPosts;
    },
    currentFilter() {
      return (
        this.statusComp.find((value) => value.name === this.filter) ||
        this.defaultStatuses.find((value) => value.name === this.filter)
      );
    },
    filterOptions() {
      const options = [
        { name: "All", human_name: this.$t("All") },
        { name: "MyIdeas", human_name: this.$t("My ideas") },
        { name: "VotedFor", human_name: this.$t("Voted for") },
      ];
      if (this.currentProjectAdmin) {
        options.push({ name: "PrivateIdeas", human_name: this.$t("Private ideas") });
      }
      return options;
    },
    csrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]');
      return meta ? meta.content : '';
    },
    bulkStatusOptions() {
      return this.postStatus || [];
    },
    bulkStatusValues() {
      return this.bulkStatusOptions.map(s => ({
        name: s.id,
        human_name: s.name,
        color: s.color
      }));
    },
    bulkStatusPlaceholder() {
      return [{ name: "", human_name: this.$t("Set status") }];
    },
    viewTabs() {
      return [
        { name: "ideas", label: this.$t("Ideas") },
        { name: "spam", label: this.$t("Spam") },
      ];
    },
    bulkDeleteLimit() {
      return BULK_DELETE_LIMIT;
    },
    bulkMergeLimit() {
      return BULK_MERGE_LIMIT;
    },
    // Selected rows resolved to loaded posts, most votes first (ties: oldest first)
    // so the natural survivor sits at the top of the merge dialog.
    bulkMergeCandidates() {
      const selected = new Set(this.selectedPostIds);
      return this.posts
        .filter(p => selected.has(p.id))
        .map(p => ({ id: p.id, title: p.title, votes: (p.cached_votes_up || 0) + (p.manual_upvotes || 0), created_at: p.created_at }))
        .sort((a, b) => b.votes - a.votes || new Date(a.created_at) - new Date(b.created_at));
    },
  },
  methods: {
    formatCount(count) {
      if (count < 1000) return `${count}`;
      const thousands = count / 1000;
      const rounded = thousands >= 100 ? Math.round(thousands) : Math.round(thousands * 10) / 10;
      return `${rounded}k`;
    },

    // ==========================================
    // Server-side Pagination Methods
    // ==========================================
    async fetchPosts(reset = false) {
      // Cancel any pending request
      if (this.fetchController) {
        this.fetchController.abort();
      }
      this.fetchController = new AbortController();

      if (reset) {
        this.posts = [];
        this.nextCursor = null;
        this.hasMore = true;
        this.showLoading = true;
      }

      // Don't fetch if no more results (unless resetting)
      if (!this.hasMore && !reset) return;

      // Don't fetch if already loading more
      if (this.isLoadingMore && !reset) return;

      this.isLoadingMore = true;

      // Build query params (spam view overrides the status/user filters)
      const params = new URLSearchParams({
        sort: this.sort,
        status: this.viewTab === 'spam' ? 'All' : this.filter,
        filter: this.viewTab === 'spam' ? 'Spam' : this.selectedFilter,
        limit: '20'
      });

      if (this.nextCursor) {
        params.append('cursor', this.nextCursor);
      }

      this.selectedTags.forEach(tag => {
        params.append('tags[]', tag);
      });

      if (this.submitterFilter) {
        params.append(
          this.submitterFilter.type === 'domain' ? 'submitter_domain' : 'submitter',
          this.submitterFilter.value
        );
      }

      try {
        const response = await fetch(
          `/boards/${this.postBoard.id}/posts_paginated?${params}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'X-CSRF-Token': this.csrfToken
            },
            signal: this.fetchController.signal
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (reset) {
          this.posts = data.posts;
          this.pruneSelectionToLoaded();
        } else {
          // Append new posts, avoiding duplicates
          const existingIds = new Set(this.posts.map(p => p.id));
          const newPosts = data.posts.filter(p => !existingIds.has(p.id));
          this.posts = [...this.posts, ...newPosts];
        }

        this.currentUserVotes = data.current_user_votes || [];
        this.nextCursor = data.next_cursor;
        this.hasMore = data.has_more;
        this.noPosts = this.posts.length === 0;
        if (typeof data.ideas_count !== 'undefined') {
          this.ideasCount = data.ideas_count;
        }
        if (typeof data.spam_count !== 'undefined') {
          this.spamCount = data.spam_count;
        }

        // Initialize computed values for display
        this.initializeTotalUpvotes();

      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was cancelled, ignore
          return;
        }
        console.error('Error fetching posts:', error);
      } finally {
        this.isLoadingMore = false;
        this.showLoading = false;
        this.initialLoadComplete = true;
      }
    },

    // Infinite scroll handler
    loadMore: function () {
      if (!this.suggestionsShown && this.hasMore && !this.isLoadingMore) {
        this.fetchPosts(false);
      }
    },

    // Debounced fetch to prevent rapid requests
    debouncedFetch() {
      clearTimeout(this.fetchDebounceTimer);
      this.fetchDebounceTimer = setTimeout(() => {
        this.fetchPosts(true);
      }, 150);
    },

    // ==========================================
    // Filter/Sort Change Handlers
    // ==========================================
    changeSort(value) {
      this.sort = value;
      this.updateUrlParam('sort', value);
      this.debouncedFetch();
    },

    changeFilter(value) {
      this.filter = value;
      this.updateUrlParam('filter', value);
      this.debouncedFetch();
    },

    changeSelectedFilter(value) {
      this.selectedFilter = value;
      this.debouncedFetch();
    },

    changeSubmitterFilter(selection) {
      this.submitterFilter = selection;
      const url = new URL(window.location.href);
      url.searchParams.delete('submitter');
      url.searchParams.delete('submitter_domain');
      if (selection) {
        url.searchParams.set(
          selection.type === 'domain' ? 'submitter_domain' : 'submitter',
          selection.value
        );
      }
      window.history.pushState(window.history.state, '', url);
      this.debouncedFetch();
    },

    // ==========================================
    // Spam view + bulk selection (admins)
    // ==========================================
    changeViewTab(tab) {
      if (this.viewTab === tab) return;
      this.viewTab = tab;
      this.clearBulkSelection();
      this.bulkError = "";
      if (tab === 'spam') {
        this.updateUrlParam('tab', 'spam');
      } else {
        this.removeUrlParam('tab');
      }
      this.fetchPosts(true);
    },

    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) {
        this.clearBulkSelection();
      }
    },

    toggleBulkSelect(postId, shiftKey = false) {
      const posts = this.paginatedPosts;
      const clickedIndex = posts.findIndex(p => p.id === postId);
      const willSelect = !this.selectedPostIds.includes(postId);

      if (shiftKey && this.lastSelectedIndex !== null && clickedIndex !== -1) {
        // Shift-click: apply the clicked checkbox's new state to the whole
        // range between the last clicked row and this one (Gmail-style).
        const start = Math.min(this.lastSelectedIndex, clickedIndex);
        const end = Math.max(this.lastSelectedIndex, clickedIndex);
        const rangeIds = posts.slice(start, end + 1).map(p => p.id);
        if (willSelect) {
          this.selectedPostIds = Array.from(new Set([...this.selectedPostIds, ...rangeIds])).slice(0, BULK_SELECT_LIMIT);
        } else {
          const rangeSet = new Set(rangeIds);
          this.selectedPostIds = this.selectedPostIds.filter(id => !rangeSet.has(id));
        }
      } else if (willSelect) {
        this.selectedPostIds.push(postId);
      } else {
        this.selectedPostIds.splice(this.selectedPostIds.indexOf(postId), 1);
      }

      this.lastSelectedIndex = clickedIndex === -1 ? null : clickedIndex;
    },

    selectAllOnPage() {
      this.selectedPostIds = this.paginatedPosts.slice(0, BULK_SELECT_LIMIT).map(p => p.id);
    },

    clearBulkSelection() {
      this.selectedPostIds = [];
      this.lastSelectedIndex = null;
    },

    // Merge and delete can skip rows server-side (already merged, spam) —
    // affected_count is the truth, and the bar unmounts on success, so the
    // outcome has to be a toast.
    notifyBulkOutcome(action, submittedCount, affectedCount) {
      if (typeof affectedCount === 'undefined') return;
      const submitted = action === 'merge' ? submittedCount - 1 : submittedCount;
      const skipped = Math.max(0, submitted - affectedCount);

      if (skipped > 0 && (action === 'merge' || action === 'delete')) {
        const key = action === 'merge'
          ? '{affected} of {submitted} ideas merged — the rest were skipped (already merged or marked as spam)'
          : '{affected} of {submitted} ideas deleted — the rest were skipped';
        this.$notify({ group: 'alert', text: this.$t(key, { affected: affectedCount, submitted }) });
      } else if (action === 'merge') {
        const key = affectedCount === 1 ? '{count} idea merged' : '{count} ideas merged';
        this.$notify({ group: 'success', text: this.$t(key, { count: affectedCount }) });
      }
    },

    // The selection must only ever reference loaded rows: whenever this.posts
    // is replaced (filter/sort/tag/search change), drop ids that left the list
    // so bulk actions can't submit posts the user no longer sees.
    pruneSelectionToLoaded() {
      if (this.selectedPostIds.length === 0) return;
      const loaded = new Set(this.posts.map(p => p.id));
      this.selectedPostIds = this.selectedPostIds.filter(id => loaded.has(id));
      this.lastSelectedIndex = null;
    },

    onBulkStatusPick(statusId) {
      if (statusId) this.runBulk('status', statusId);
    },

    openBulkMergeDialog() {
      const candidates = this.bulkMergeCandidates;
      if (candidates.length < 2) return;
      this.bulkMergeTargetId = candidates[0].id;
      this.showBulkMergeConfirm = true;
    },

    // postIds overrides the raw selection for actions that confirmed a specific
    // list (merge dialog): the server must only receive ids the user saw.
    async runBulk(action, statusId = null, targetPostId = null, postIds = null) {
      const ids = postIds || this.selectedPostIds;
      if (ids.length === 0 || this.bulkBusy) return;
      this.bulkBusy = true;
      this.bulkError = "";

      try {
        const response = await fetch(`/boards/${this.postBoard.id}/posts/bulk_update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            post_ids: ids,
            bulk_action: action,
            status_id: statusId,
            target_post_id: targetPostId
          })
        });
        const data = await response.json();

        if (response.ok && data.success) {
          if (typeof data.ideas_count !== 'undefined') {
            this.ideasCount = data.ideas_count;
          }
          if (typeof data.spam_count !== 'undefined') {
            this.spamCount = data.spam_count;
          }
          const removedIds = new Set(ids);
          this.notifyBulkOutcome(action, ids.length, data.affected_count);
          this.clearBulkSelection();
          this.showBulkDeleteConfirm = false;
          this.showBulkMergeConfirm = false;
          this.bulkMergeTargetId = null;

          if (action === 'status' || action === 'merge') {
            // Status may or may not remove rows from the current filter — refetch.
            // Merge removes the sources AND changes the survivor's counts — refetch.
            this.fetchPosts(true);
          } else {
            // Spam/not-spam/delete always remove rows from the current view:
            // drop them in place so the transition-group animates them out.
            this.posts = this.posts.filter(p => !removedIds.has(p.id));
            this.noPosts = this.posts.length === 0;
            if (this.posts.length < 10 && this.hasMore) {
              this.fetchPosts(false);
            }
          }
        } else {
          this.bulkError = data.error || this.$t('Something went wrong. Please try again');
          if (action === 'merge') {
            // The board may have changed under us (target merged/flagged
            // elsewhere) — resync so stale rows can't be re-submitted.
            this.showBulkMergeConfirm = false;
            this.fetchPosts(true);
          }
        }
      } catch (error) {
        this.bulkError = this.$t('Something went wrong. Please try again');
      } finally {
        this.bulkBusy = false;
      }
    },

    updateSelectedTags(newTags) {
      this.selectedTags = newTags;
      if (newTags.length > 0) {
        this.updateUrlParam('tags', newTags.join(','));
      } else {
        this.removeUrlParam('tags');
      }
      this.debouncedFetch();
    },

    // ==========================================
    // URL State Management
    // ==========================================
    applyUrlParams() {
      const url = new URL(window.location.href);
      const sort = url.searchParams.get('sort');
      const filter = url.searchParams.get('filter');
      const tags = url.searchParams.get('tags');

      if (sort && this.sortOptions.some(o => o.name === sort)) {
        this.sort = sort;
      }
      if (filter) {
        this.filter = filter;
      }
      if (tags) {
        this.selectedTags = tags.split(',').filter(t => t);
      }
      if (url.searchParams.get('tab') === 'spam' && this.currentProjectAdmin) {
        this.viewTab = 'spam';
      }
      // Submitter filters are team-only; the server ignores them for everyone
      // else, so don't show a chip that isn't filtering anything.
      if (this.currentProjectAdmin) {
        const submitter = url.searchParams.get('submitter');
        const submitterDomain = url.searchParams.get('submitter_domain');
        if (submitter) {
          this.submitterFilter = { type: 'email', value: submitter, label: submitter };
        } else if (submitterDomain) {
          this.submitterFilter = { type: 'domain', value: submitterDomain, label: '@' + submitterDomain };
        }
      }
    },

    updateUrlParam(key, value) {
      const url = new URL(window.location.href);
      url.searchParams.set(key, value);
      window.history.pushState(window.history.state, '', url);
    },

    removeUrlParam(key) {
      const url = new URL(window.location.href);
      url.searchParams.delete(key);
      window.history.pushState(window.history.state, '', url);
    },

    // ==========================================
    // Event Handlers
    // ==========================================
    setupEventListeners() {
      var ctx = this;

      EventBus.$on("showLoader", function () {
        ctx.showLoading = true;
      });

      EventBus.$on("showSuggestions", function (data) {
        if (data && data.length > 0) {
          ctx.suggestionsShown = true;
          ctx.posts = data;
          ctx.pruneSelectionToLoaded();
          ctx.initializeTotalUpvotes();
          ctx.showLoading = false;
          ctx.noPosts = false;
        } else {
          ctx.suggestionsShown = false;
          ctx.fetchPosts(true);
        }
      });

      EventBus.$on("hideSuggestions", function () {
        if (ctx.suggestionsShown) {
          ctx.suggestionsShown = false;
          ctx.fetchPosts(true);
        }
      });

      EventBus.$on("postVotes", (data) => {
        let upvotedPost = this.posts.find((post) => post.id === data.id);
        if (upvotedPost) {
          upvotedPost.total_upvotes = data.total;
          upvotedPost.cached_votes_up = data.cached_votes_up;
        }
      });

      EventBus.$on("postCreated", () => {
        // Refresh to show new post
        this.fetchPosts(true);
      });
    },

    // ==========================================
    // Utility Methods
    // ==========================================
    parameterize: function (input) {
      if (!input) return '';
      return input
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
    },

    initializeTotalUpvotes(posts = this.posts) {
      posts.forEach((post) => {
        if (post.total_upvotes === undefined) {
          post.total_upvotes = (post.cached_votes_up || 0) + (post.manual_upvotes || 0);
        }
      });
    },

    initializeStatusDropdown() {
      // Get default status objects
      this.defaultStatuses = this.postStatus.filter((status) => {
        return ["Done"].includes(status.name);
      });
      this.defaultStatuses.unshift({
        name: "Not done",
        human_name: "Not done",
        color: "#828282",
      });
      this.defaultStatuses.unshift({
        name: "All",
        human_name: "All Statuses",
        color: "#828282",
      });
    },
  },
  beforeDestroy() {
    // Cancel pending request
    if (this.fetchController) {
      this.fetchController.abort();
    }

    window.removeEventListener("click", this.manageOutsideHandler, true);

    // Clear debounce timer
    clearTimeout(this.fetchDebounceTimer);

    // Remove event listeners
    EventBus.$off("showLoader");
    EventBus.$off("showSuggestions");
    EventBus.$off("hideSuggestions");
    EventBus.$off("postVotes");
    EventBus.$off("postCreated");
  },
};
</script>

<style scoped lang="scss">
// Help Scout's chat bubble is fixed to the bottom-right on phones and
// covered the bar's Delete button.
@media (max-width: 639px) {
  #bulk-action-bar {
    bottom: 5.5rem;
  }
}

.bulkbar-enter-active,
.bulkbar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.bulkbar-enter,
.bulkbar-leave-to {
  opacity: 0;
  transform: translateY(0.75rem);
}

// Match the dropdown trigger to the bar's buttons (they are text-sm px-4 py-2).
.bulk-status-dropdown ::v-deep #select-one-box {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  justify-content: center;
}

body {
  background: #e9e9e9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-div .wrapper {
  margin-top: 23px;
  margin-left: 10px;
  padding: 10px;
  background: #fff;
  width: 360px;
  height: 640px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.wrapper .wrapper-cell {
  display: flex;
  margin-bottom: 21px;
}

@keyframes placeHolderShimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.animated-background {
  animation-duration: 1.25s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: #f6f6f6;
  background: linear-gradient(to right, #f6f6f6 8%, #f0f0f0 18%, #f6f6f6 33%);
  background-size: 800px 104px;
  height: 96px;
  position: relative;
}

.wrapper .wrapper-cell .image {
  width: 55px;
  height: 67px;
  border-radius: 3px;
  @extend .animated-background;
}

.wrapper .wrapper-cell .text {
  margin-left: 20px;
}

.wrapper .wrapper-cell .text .text-line-1 {
  position: absolute;
  width: 470px;
  height: 22px;
  border-radius: 3px;
  @extend .animated-background;
}

.wrapper .wrapper-cell .text .text-line-2 {
  position: absolute;
  width: 280px;
  height: 22px;
  border-radius: 3px;
  margin-top: 30px;
  @extend .animated-background;
}

@media only screen and (max-width: 767px) {
  .wrapper .wrapper-cell .text .text-line-1 {
    width: 320px;
  }
  .wrapper .wrapper-cell .text .text-line-2 {
    width: 210px;
  }
}

@media only screen and (max-width: 480px) {
  .wrapper .wrapper-cell .text .text-line-1 {
    width: 250px;
  }
  .wrapper .wrapper-cell .text .text-line-2 {
    width: 170px;
  }
}

@media only screen and (max-width: 400px) {
  .wrapper .wrapper-cell .text .text-line-1 {
    width: 220px;
  }
  .wrapper .wrapper-cell .text .text-line-2 {
    width: 145px;
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter,
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.list-enter-to,
.list-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.list-move {
  transition: transform 0.5s ease;
}

/* For the initial appear animation */
.list-appear-active {
  transition: all 0.5s ease;
}

.list-appear-from {
  opacity: 0;
  transform: translateY(-20px);
}

.list-appear-to {
  opacity: 1;
  transform: translateY(0);
}

.scale-75 {
  transform: scale(0.75);
}
</style>
